import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO, subDays, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Filter, Plus, Trash2, Edit, Eye, FileText, Wallet, Package, Scissors, 
  AlertTriangle, BarChart3, Shield, 
  Database, RefreshCw, TrendingUp, Users, Activity as ActivityIcon, Clock
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Input from '../components/ui/Input';
import { ActivityLog } from '../types';
import { supabase } from '../lib/supabase';

const Activity: React.FC = () => {
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<{ id: string; email: string; role: string }[]>([]);
  const [viewMode, setViewMode] = useState<'table' | 'analytics'>('table');
  const [usersError, setUsersError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  
  // Filters
  const [filters, setFilters] = useState({
    userId: 'all',
    userRole: 'all',
    period: 'month',
    startDate: '',
    endDate: '',
    searchQuery: '',
  });

  // Fetch activity logs
  useEffect(() => {
    const fetchActivityLogs = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('activity_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1000); // Limit to prevent loading too many records
        
        if (error) throw error;
        setActivityLogs(data || []);
      } catch (err: any) {
        console.error('Error fetching activity logs:', err);
        setError(err.message || 'Error al cargar los registros de actividad');
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivityLogs();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        console.log('Fetching users from safe_users view...');
        
        // Use safe_users view directly since admin access is restricted
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('safe_users')
          .select('*');
        
        if (fallbackError) {
          console.error('Error fetching users from safe_users:', fallbackError);
          
          // If safe_users doesn't exist or user doesn't have access, try to get users from activity logs
          if (fallbackError.code === 'PGRST116' || fallbackError.message?.includes('relation') || fallbackError.message?.includes('does not exist')) {
            console.log('safe_users view not accessible, extracting users from activity logs');
            
            // Extract unique user IDs from activity logs
            const uniqueUserIds = new Set<string>();
            activityLogs.forEach(log => {
              if (log.user_id) {
                uniqueUserIds.add(log.user_id);
              }
            });
            
            // Create minimal user objects from activity logs
            const minimalUsers = Array.from(uniqueUserIds).map(userId => ({
              id: userId,
              email: `usuario.${userId.slice(0, 8)}@empresa.com`,
              role: 'Usuario'
            }));
            
            setUsers(minimalUsers);
            setUsersError('Información de usuarios limitada. Se muestran IDs extraídos de la actividad.');
            return;
          }
          
          setUsersError('Error al cargar la información de usuarios');
          setUsers([]);
          return;
        }
        
        console.log('Users fetched successfully from safe_users:', fallbackData?.length || 0);
        
        const formattedUsers = (fallbackData || []).map((user: any) => ({
          id: user.id,
          email: user.email || `usuario.${user.id.slice(0, 8)}@empresa.com`,
          role: user.role || 'Usuario',
        }));
        
        setUsers(formattedUsers);
        setUsersError(null);
      } catch (err) {
        console.error('Error fetching users:', err);
        
        // Fallback: extract users from activity logs
        const uniqueUserIds = new Set<string>();
        activityLogs.forEach(log => {
          if (log.user_id) {
            uniqueUserIds.add(log.user_id);
          }
        });
        
        const minimalUsers = Array.from(uniqueUserIds).map(userId => ({
          id: userId,
          email: `usuario.${userId.slice(0, 8)}@empresa.com`,
          role: 'Usuario'
        }));
        
        setUsers(minimalUsers);
        setUsersError('Información de usuarios limitada. Se muestran IDs extraídos de la actividad.');
      }
    };

    fetchUsers();
  }, [activityLogs]); // Add activityLogs as dependency

  // Apply date filters based on period
  useEffect(() => {
    if (filters.period === 'custom') {
      // Don't modify dates for custom period
      return;
    }
    
    const now = new Date();
    let startDate = '';
    
    switch (filters.period) {
      case 'today':
        startDate = format(now, 'yyyy-MM-dd');
        break;
      case '7days':
        startDate = format(subDays(now, 7), 'yyyy-MM-dd');
        break;
      case '30days':
        startDate = format(subDays(now, 30), 'yyyy-MM-dd');
        break;
      case 'month':
        startDate = format(startOfMonth(now), 'yyyy-MM-dd');
        break;
      case 'quarter':
        startDate = format(startOfQuarter(now), 'yyyy-MM-dd');
        break;
      case 'year':
        startDate = format(startOfYear(now), 'yyyy-MM-dd');
        break;
      default:
        // 'all' - no date filtering
        startDate = '';
        break;
    }
    
    setFilters(prev => ({
      ...prev,
      startDate,
      endDate: startDate ? format(now, 'yyyy-MM-dd') : '',
    }));
  }, [filters.period]);

  // Get unique user roles
  const userRoles = useMemo(() => {
    const roles = new Set<string>();
    (users || []).forEach(user => {
      if (user.role) {
        roles.add(user.role);
      }
    });
    return Array.from(roles);
  }, [users]);

  // Filter logs
  const filteredLogs = useMemo(() => {
    return (activityLogs || []).filter(log => {
      // EXCLUIR logs con user_id null o vacío
      if (!log.user_id || log.user_id === null || log.user_id === '') {
        return false;
      }

      // EXCLUIR usuarios con rol Administrador o Super Admin (solo actividad del equipo)
      const user = (users || []).find(u => u.id === log.user_id);
      if (user && (user.role === 'Administrador' || user.role === 'Super Admin')) {
        return false;
      }

      // Filter by user
      if (filters.userId !== 'all' && log.user_id !== filters.userId) {
        return false;
      }

      // Filter by user role
      if (filters.userRole !== 'all') {
        if (!user || user.role !== filters.userRole) {
          return false;
        }
      }

      // Filter by date range
// Filter by date range
if (filters.startDate && filters.endDate) {
  // Simple date comparison
  const logDate = parseISO(log.created_at);
  const startDate = new Date(filters.startDate + 'T00:00:00');
  const endDate = new Date(filters.endDate + 'T23:59:59');
  
  if (logDate < startDate || logDate > endDate) {
    return false;
  }
}


      // Filter by search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const user = (users || []).find(u => u.id === log.user_id);
        const userEmail = user?.email.toLowerCase() || '';
        
        return (
          log.action.toLowerCase().includes(query) ||
          log.details.toLowerCase().includes(query) ||
          userEmail.includes(query)
        );
      }

      return true;
    });
  }, [activityLogs, filters, users]);


  // Pagination logic
  const totalPages = Math.ceil((filteredLogs?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = (filteredLogs || []).slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Get user email by ID
  const getUserEmail = (userId: string): string => {
    const user = (users || []).find(u => u.id === userId);
    const email = user?.email || `usuario.${userId.slice(0, 8)}@empresa.com`;
    
    // Cortar email después del @ para mostrar solo la parte del usuario
    return email.split('@')[0];
  };

  // Get user role by ID
  const getUserRole = (userId: string): string => {
    const user = (users || []).find(u => u.id === userId);
    return user?.role || 'Usuario';
  };

  // Translate database terms to Spanish
  const translateDbTerms = (text: string): string => {
    const translations: { [key: string]: string } = {
      // Entity types
      'sample_tasks': 'tareas de muestra',
      'production_tasks': 'tareas de producción',
      'suppliers': 'proveedores',
      'clients': 'clientes',
      'samples': 'muestras',
      'productions': 'producciones',
      'express_samples': 'muestras express',
      'express_orders': 'pedidos express',
      'invoices': 'facturas',
      'payments': 'pagos',
      'expenses': 'gastos',
      'logistics_trips': 'viajes logísticos',
      'logistics_drivers': 'conductores',
      'crm_leads': 'prospectos',
      'crm_reminders': 'recordatorios',
      'activity_logs': 'registros de actividad',
      'users': 'usuarios',
      
      // Actions
      'create': 'crear',
      'created': 'creado',
      'creation': 'creación',
      'update': 'actualizar',
      'updated': 'actualizado',
      'actualización': 'actualización',
      'delete': 'eliminar',
      'deleted': 'eliminado',
      'view': 'ver',
      'viewed': 'visto',
      
      // Common words
      'task': 'tarea',
      'tasks': 'tareas',
      'sample': 'muestra',
      'production': 'producción',
      'supplier': 'proveedor',
      'client': 'cliente',
      'invoice': 'factura',
      'payment': 'pago',
      'expense': 'gasto',
      'trip': 'viaje',
      'driver': 'conductor',
      'lead': 'prospecto',
      'reminder': 'recordatorio',
      'user': 'usuario'
    };

    let translatedText = text;
    
    // Replace all database terms with Spanish equivalents
    Object.keys(translations).forEach(term => {
      const regex = new RegExp(`\\b${term}\\b`, 'gi');
      translatedText = translatedText.replace(regex, translations[term]);
    });
    
    return translatedText;
  };

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!filteredLogs || filteredLogs.length === 0) {
      return {
        totalActions: 0,
        uniqueUsers: 0,
        actionsByType: {},
        actionsByUser: {},
        actionsByRole: {},
        actionsByDay: {},
        mostActiveUser: null,
        mostActiveRole: null,
        averageActionsPerDay: 0,
        totalDays: 0
      };
    }

    const actionsByType: { [key: string]: number } = {};
    const actionsByUser: { [key: string]: number } = {};
    const actionsByRole: { [key: string]: number } = {};
    const actionsByDay: { [key: string]: number } = {};
    const uniqueUserIds = new Set<string>();
    const uniqueDays = new Set<string>();

    filteredLogs.forEach(log => {
      // Count by action type
      actionsByType[log.action_type] = (actionsByType[log.action_type] || 0) + 1;
      
      // Count by user
      actionsByUser[log.user_id] = (actionsByUser[log.user_id] || 0) + 1;
      uniqueUserIds.add(log.user_id);
      
      // Count by role
      const userRole = getUserRole(log.user_id);
      actionsByRole[userRole] = (actionsByRole[userRole] || 0) + 1;
      
      // Count by day
      const day = format(parseISO(log.created_at), 'yyyy-MM-dd');
      actionsByDay[day] = (actionsByDay[day] || 0) + 1;
      uniqueDays.add(day);
    });

    // Find most active user
    const mostActiveUserId = Object.keys(actionsByUser).reduce((a, b) => 
      actionsByUser[a] > actionsByUser[b] ? a : b, '');
    const mostActiveUser = mostActiveUserId ? {
      id: mostActiveUserId,
      email: getUserEmail(mostActiveUserId),
      actions: actionsByUser[mostActiveUserId]
    } : null;

    // Find most active role
    const mostActiveRoleName = Object.keys(actionsByRole).reduce((a, b) => 
      actionsByRole[a] > actionsByRole[b] ? a : b, '');
    const mostActiveRole = mostActiveRoleName ? {
      name: mostActiveRoleName,
      actions: actionsByRole[mostActiveRoleName]
    } : null;

    return {
      totalActions: filteredLogs.length,
      uniqueUsers: uniqueUserIds.size,
      actionsByType,
      actionsByUser,
      actionsByRole,
      actionsByDay,
      mostActiveUser,
      mostActiveRole,
      averageActionsPerDay: uniqueDays.size > 0 ? Math.round(filteredLogs.length / uniqueDays.size * 10) / 10 : 0,
      totalDays: uniqueDays.size
    };
  }, [filteredLogs, users]);

  // Group logs by day for table display
  const logsGroupedByDay = useMemo(() => {
    if (!paginatedLogs || paginatedLogs.length === 0) return [];

    const grouped: { [key: string]: ActivityLog[] } = {};
    
    paginatedLogs.forEach(log => {
      const day = format(parseISO(log.created_at), 'yyyy-MM-dd');
      if (!grouped[day]) {
        grouped[day] = [];
      }
      grouped[day].push(log);
    });

    // Sort days in descending order and return as array
    return Object.keys(grouped)
      .sort((a, b) => b.localeCompare(a))
      .map(day => ({
        date: day,
        logs: grouped[day].sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      }));
  }, [paginatedLogs]);

// Format date to Argentina timezone
// Format date to Argentina timezone
const formatArgentinaDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    
    // Sumar 3 horas para Buenos Aires (UTC-3 pero sumamos porque la DB guarda en UTC)
    const argentinaDate = new Date(date.getTime() + (3 * 60 * 60 * 1000));
    
    return format(argentinaDate, "dd/MM/yyyy HH:mm", { locale: es });
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return 'Fecha inválida';
  }
};

  // Get action icon
  const getActionIcon = (log: ActivityLog) => {
    const iconProps = { className: "h-5 w-5" };
    
    if (log.action_type === 'create') {
      return <Plus {...iconProps} />;
    } else if (log.action_type === 'update') {
      return <Edit {...iconProps} />;
    } else if (log.action_type === 'delete') {
      return <Trash2 {...iconProps} />;
    } else if (log.action_type === 'view') {
      return <Eye {...iconProps} />;
    }
    
    // Entity-specific icons
    switch (log.entity_type) {
      case 'invoice':
        return <FileText {...iconProps} />;
      case 'payment':
        return <Wallet {...iconProps} />;
      case 'express':
      case 'express_stock':
        return <Package {...iconProps} />;
      case 'sample':
      case 'express_sample':
        return <Scissors {...iconProps} />;
      default:
        return <AlertTriangle {...iconProps} />;
    }
  };

  // Get action color class
  const getActionColorClass = (log: ActivityLog): string => {
    if (log.action_type === 'create') {
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    } else if (log.action_type === 'delete') {
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    } else if (log.action_type === 'update') {
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    } else {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      userId: 'all',
      userRole: 'all',
      period: 'month',
      startDate: '',
      endDate: '',
      searchQuery: '',
    });
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <ActivityIcon className="mr-2 h-6 w-6 text-red-600 dark:text-red-400" />
          Log de Actividad
          </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={() => window.location.reload()}
            size="sm"
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={() => setShowFilters(!showFilters)}
            size="sm"
          >
            Filtros
          </Button>
              <Button
            variant="outline"
            icon={<Filter className="h-4 w-4" />}
            onClick={resetFilters}
            size="sm"
          >
            Limpiar
              </Button>
            </div>
            </div>

      {/* Filters Dropdown */}
      {showFilters && (
      <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
                label="Período"
                value={filters.period}
                onChange={(value) => setFilters(prev => ({ ...prev, period: value }))}
              options={[
                  { value: 'today', label: 'Hoy' },
                  { value: '7days', label: 'Últimos 7 días' },
                  { value: '30days', label: 'Últimos 30 días' },
                  { value: 'month', label: 'Este mes' },
                  { value: 'quarter', label: 'Este trimestre' },
                  { value: 'year', label: 'Este año' },
                  { value: 'custom', label: 'Personalizado' },
              ]}
            />

            <Select
              label="Rol"
              value={filters.userRole}
              onChange={(value) => setFilters(prev => ({ ...prev, userRole: value }))}
              options={[
                { value: 'all', label: 'Todos los roles' },
                ...(userRoles || []).map(role => ({
                  value: role,
                  label: role
                }))
              ]}
            />

            <Select
                label="Usuario"
                value={filters.userId}
                onChange={(value) => setFilters(prev => ({ ...prev, userId: value }))}
              options={[
                  { value: 'all', label: 'Todos los usuarios' },
                  ...(users || []).map(user => ({
                    value: user.id,
                    label: user.email
                  }))
              ]}
            />
          </div>

          {filters.period === 'custom' && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="Desde"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
              <Input
                label="Hasta"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1">
        <div className="flex">
          <button
            onClick={() => setViewMode('table')}
            className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Database className="h-4 w-4 mr-2" />
            Tabla Detallada
          </button>
          <button
            onClick={() => setViewMode('analytics')}
            className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'analytics'
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Estadísticas
          </button>
              </div>
              </div>

      {/* Users Error Warning */}
      {usersError && (
        <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
                        <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {usersError}
                      </p>
                    </div>
          </CardContent>
        </Card>
      )}



      {/* Content based on view mode */}
      {viewMode === 'table' && (
        <Card>
          <CardHeader title="Tabla Detallada de Actividad" />
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md text-red-700 dark:text-red-300">
                {error}
              </div>
            ) : (filteredLogs?.length || 0) === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No se encontraron registros de actividad del equipo</p>
                <p className="text-sm mt-2">Los administradores están excluidos de este log</p>
              </div>
            ) : (
              <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <tbody className="bg-white dark:bg-gray-900">
                      {logsGroupedByDay.map((dayGroup, dayIndex) => (
                        <React.Fragment key={dayGroup.date}>
                          {/* Day Separator */}
                          <tr className="bg-gray-100 dark:bg-gray-800">
                            <td colSpan={5} className="px-3 py-3">
                              <div className="flex items-center">
                                <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                                <div className="px-3">
                                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {format(parseISO(dayGroup.date), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es })}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    ({dayGroup.logs.length} acciones)
                                  </span>
                                </div>
                                <div className="flex-1 border-t border-gray-300 dark:border-gray-600"></div>
                              </div>
                            </td>
                    </tr>
                          {/* Logs for this day */}
                          {dayGroup.logs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="text-xs font-medium text-gray-900 dark:text-white">
                            {getUserEmail(log.user_id)}
                          </div>
                        </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className="px-1.5 py-0.5 inline-flex text-xs leading-4 font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            {getUserRole(log.user_id)}
                          </span>
                        </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className={`px-1.5 py-0.5 inline-flex items-center text-xs leading-4 font-medium rounded-full ${getActionColorClass(log)}`}>
                            <span className="mr-1">{getActionIcon(log)}</span>
                                  {translateDbTerms(log.action)}
                          </div>
                        </td>
                              <td className="px-3 py-2">
                                <div className="text-xs text-gray-900 dark:text-white max-w-xs truncate">
                                  {translateDbTerms(log.details)}
                          </div>
                        </td>
                              <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {formatArgentinaDate(log.created_at)}
                        </td>
                      </tr>
                          ))}
                        </React.Fragment>
                    ))}
                  </tbody>
                </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Siguiente
                      </Button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Mostrando{' '}
                          <span className="font-medium">{startIndex + 1}</span>
                          {' '}a{' '}
                          <span className="font-medium">{Math.min(endIndex, filteredLogs?.length || 0)}</span>
                          {' '}de{' '}
                          <span className="font-medium">{filteredLogs?.length || 0}</span>
                          {' '}resultados
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <Button
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md"
                          >
                            Anterior
                          </Button>
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            if (page > totalPages) return null;
                            return (
                              <Button
                                key={page}
                                variant={currentPage === page ? 'primary' : 'outline'}
                                onClick={() => setCurrentPage(page)}
                                className="relative inline-flex items-center px-3 py-2"
                              >
                                {page}
                              </Button>
                            );
                          })}
                          <Button
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md"
                          >
                            Siguiente
                          </Button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {viewMode === 'analytics' && (
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <ActivityIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Acciones</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.totalActions}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Usuarios Activos</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.uniqueUsers}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Promedio/Día</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.averageActionsPerDay}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Días Activos</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.totalDays}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Statistics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Actions by Type */}
          <Card>
              <CardHeader title="Acciones por Tipo" />
            <CardContent>
                <div className="space-y-3">
                  {Object.entries(statistics.actionsByType).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3 ${
                          type === 'create' ? 'bg-green-500' :
                          type === 'update' ? 'bg-blue-500' :
                          type === 'delete' ? 'bg-red-500' :
                          'bg-gray-500'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                          {translateDbTerms(type)}
                          </span>
                        </div>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {count}
                      </span>
                      </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Actions by Role */}
            <Card>
              <CardHeader title="Actividad por Rol" />
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(statistics.actionsByRole)
                    .sort(([,a], [,b]) => (b as number) - (a as number))
                    .map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {role || 'Sin rol'}
                      </span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {count}
                      </span>
                    </div>
                  ))}
                  {Object.keys(statistics.actionsByRole).length === 0 && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">No hay datos de roles disponibles</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Activity */}
          <Card>
            <CardHeader title="Actividad por Usuario" />
            <CardContent>
              <div className="space-y-4">
                {Object.entries(statistics.actionsByUser)
                  .sort(([,a], [,b]) => (b as number) - (a as number))
                  .slice(0, 10)
                  .map(([userId, count]) => {
                    const isMostActive = statistics.mostActiveUser?.id === userId;
                    const userEmail = getUserEmail(userId);
                    const userRole = getUserRole(userId);
                    
                    return (
                      <div key={userId} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center">
                          {isMostActive && (
                            <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                          )}
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {userEmail}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                              {userRole} {isMostActive && '• Más activo'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {count}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                            acciones
                      </p>
                    </div>
                  </div>
                    );
                  })}
                {Object.keys(statistics.actionsByUser).length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No hay datos de usuarios disponibles</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Daily Activity */}
          <Card>
            <CardHeader title="Actividad por Día" />
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(statistics.actionsByDay)
                  .sort(([a], [b]) => b.localeCompare(a))
                  .slice(0, 15)
                  .map(([day, count]) => (
                    <div key={day} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {format(parseISO(day), "EEEE, dd 'de' MMMM", { locale: es })}
                      </span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {count}
                      </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Activity;